import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, Square, Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/auth";
import { playVoice, stopVoice } from "@/lib/ariel";

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const fmt = (s) => new Date(`${s}T12:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "long" });

export default function DailyWhisper({ gate = 0 }) {
  const [w, setW] = useState(null);
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [kept, setKept] = useState([]);
  const [showKept, setShowKept] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get("/ariel/whisper", { params: { date: today(), gate } })
      .then(({ data }) => alive && setW(data?.text ? data : null))
      .catch(() => alive && setFailed(true));
    api.get("/ariel/whispers/kept").then(({ data }) => alive && setKept(Array.isArray(data) ? data : [])).catch(() => {});
    return () => {
      alive = false;
      stopVoice();
    };
  }, [gate]);
  const hear = async () => {
    if (playing) {
      stopVoice();
      setPlaying(false);
      return;
    }
    setPlaying(true);
    try {
      await playVoice(w.text);
    } catch {
      toast.error("Ariel’s voice couldn’t play just now.");
    }
    setPlaying(false);
  };
  const keep = async () => {
    const next = !w.kept;
    try {
      await api.put("/ariel/whisper/keep", { date: w.date, kept: next });
      setW({ ...w, kept: next });
      setKept((k) => (next ? [{ ...w, kept: true }, ...k.filter((x) => x.date !== w.date)] : k.filter((x) => x.date !== w.date)));
      if (next) toast.success("Kept among your whispers.");
    } catch {
      toast.error("That didn’t save. Please try again.");
    }
  };
  if (failed) return null;
  return (
    <section data-testid="daily-whisper" className="mt-10 relative border border-[#D9B47A]/25 bg-[#120C0A] p-6 overflow-hidden">
      <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#D9B47A]/10 blur-3xl" aria-hidden="true" />
      <div className="flex items-center gap-3">
        <img src="/images/goddess-ariel-avatar.jpg" alt="Goddess Ariel" className="w-10 h-10 rounded-full object-cover border border-[#D9B47A]/40" />
        <div>
          <p className="font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#D9B47A]">Today’s whisper</p>
          <p className="text-[12px] text-[#A8998C]">from Goddess Ariel</p>
        </div>
        {w && (
          <button data-testid="daily-whisper-hear" onClick={hear} className="ml-auto inline-flex items-center gap-2 rounded-full border border-[#D9B47A]/40 px-4 py-2 font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#D9B47A] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors">
            {playing ? <><Square className="w-3.5 h-3.5" /> Stop</> : <><Volume2 className="w-3.5 h-3.5" /> Hear</>}
          </button>
        )}
      </div>
      {w ? (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} data-testid="daily-whisper-text" className="mt-5 font-display italic text-2xl leading-snug">{w.text}</motion.p>
      ) : (
        <p className="mt-5 flex items-center gap-2 text-[#A8998C]"><Loader2 className="w-4 h-4 animate-spin text-[#D9B47A]" /> Ariel is whispering…</p>
      )}
      {w && (
        <div className="mt-5 flex items-center gap-3">
          <button data-testid="daily-whisper-keep" aria-pressed={w.kept} onClick={keep} className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 font-tt-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${w.kept ? "bg-[#C2414F] border-[#C2414F] text-[#F2E8DF]" : "border-[#D9B47A]/40 text-[#D9B47A] hover:border-[#D9B47A]"}`}>
            <Heart className={`w-3.5 h-3.5 ${w.kept ? "fill-current" : ""}`} /> {w.kept ? "Kept" : "Keep"}
          </button>
        </div>
      )}
      {kept.length > 0 && (
        <div className="mt-5 border-t border-[#D9B47A]/15 pt-4">
          <button data-testid="daily-whisper-kept-toggle" onClick={() => setShowKept(!showKept)} className="text-sm text-[#A8998C] underline underline-offset-4 hover:text-[#F2E8DF]">
            {showKept ? "Hide" : "Your kept whispers"} ({kept.length})
          </button>
          {showKept && (
            <ul data-testid="daily-whisper-kept" className="mt-4 space-y-4">
              {kept.map((k) => (
                <li key={k.date}>
                  <p className="font-tt-mono text-[10px] uppercase tracking-[0.2em] text-[#A8998C]">{fmt(k.date)}</p>
                  <p className="mt-1 font-display italic text-lg leading-snug text-[#DCCFC3]">{k.text}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
